/**
 * Grillion Search
 * Opens/closes the predictive search modal from the header search icon,
 * handles iOS Safari keyboard focus, popular-search pills and the
 * "View all results" footer visibility.
 *
 * Works alongside the theme's <dialog-component> (dialog.js) and
 * <predictive-search-component> (predictive-search.js). Every path has a
 * native <dialog> fallback so the modal still works while modules load.
 */
(function () {
  'use strict';

  if (window.__grillionSearchInit) return;
  window.__grillionSearchInit = true;

  var MODAL_ID = 'search-modal';
  var OPEN_SELECTOR = '[data-grillion-search-open]';
  var INPUT_SELECTOR = '.search-input, input[type="search"]';

  var isIOS =
    /iP(ad|hone|od)/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  function getModal() {
    return document.getElementById(MODAL_ID);
  }

  function getDialog(modal) {
    modal = modal || getModal();
    return modal ? modal.querySelector('dialog') : null;
  }

  function getInput(modal) {
    modal = modal || getModal();
    return modal ? modal.querySelector(INPUT_SELECTOR) : null;
  }

  function setTriggersExpanded(expanded) {
    var triggers = document.querySelectorAll(OPEN_SELECTOR);
    for (var i = 0; i < triggers.length; i++) {
      triggers[i].setAttribute('aria-expanded', expanded ? 'true' : 'false');
    }
  }

  /**
   * iOS Safari only raises the keyboard when focus() runs synchronously inside
   * a user gesture. dialog.js opens the modal in a requestAnimationFrame, so we
   * focus a hidden proxy input now and hand focus over once the dialog is open.
   */
  function focusProxyForIOS() {
    if (!isIOS) return null;
    var proxy = document.createElement('input');
    proxy.type = 'text';
    proxy.setAttribute('aria-hidden', 'true');
    proxy.setAttribute('tabindex', '-1');
    proxy.className = 'grillion-search-focus-proxy';
    document.body.appendChild(proxy);
    try {
      proxy.focus({ preventScroll: true });
    } catch (e) {
      proxy.focus();
    }
    return proxy;
  }

  function focusInput(proxy) {
    var attempts = 0;

    function tryFocus() {
      var dialog = getDialog();
      var input = getInput();
      attempts++;

      if (dialog && dialog.open && input) {
        try {
          input.focus({ preventScroll: true });
        } catch (e) {
          input.focus();
        }
        if (proxy && proxy.parentNode) proxy.parentNode.removeChild(proxy);
        return;
      }

      if (attempts < 20) {
        window.setTimeout(tryFocus, 25);
      } else if (proxy && proxy.parentNode) {
        proxy.parentNode.removeChild(proxy);
      }
    }

    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(tryFocus);
    });
  }

  function openSearch(event) {
    var modal = getModal();
    var dialog = getDialog(modal);

    // No modal on the page: let the link fall through to /search
    if (!modal || !dialog) return;

    if (event) event.preventDefault();
    if (dialog.open) return;

    var proxy = focusProxyForIOS();

    if (typeof modal.showDialog === 'function') {
      modal.showDialog();
    } else if (typeof dialog.showModal === 'function') {
      dialog.showModal();
    } else {
      // Very old browsers without <dialog>: go to the search page
      if (proxy && proxy.parentNode) proxy.parentNode.removeChild(proxy);
      var trigger = event && event.currentTarget;
      window.location.href = (trigger && trigger.getAttribute('href')) || '/search';
      return;
    }

    setTriggersExpanded(true);
    focusInput(proxy);
  }

  function closeSearch() {
    var modal = getModal();
    var dialog = getDialog(modal);
    if (!dialog || !dialog.open) return;

    if (modal && typeof modal.closeDialog === 'function') {
      modal.closeDialog();
    } else if (typeof dialog.close === 'function') {
      dialog.close();
    }
  }

  /**
   * Shows the "View all results" footer only when real results are rendered.
   * Avoids relying on :has() so older Safari versions behave the same.
   */
  function syncFooter(modal) {
    var footer = modal.querySelector('.predictive-search-form__footer');
    var content = modal.querySelector('.predictive-search-form__content');
    if (!footer || !content) return;

    var hasResults =
      !!content.querySelector('[data-search-results]') &&
      !content.querySelector('.predictive-search-results__no-results');

    footer.classList.toggle('is-visible', hasResults);
  }

  function observeResults(modal) {
    var content = modal.querySelector('.predictive-search-form__content');
    if (!content || typeof MutationObserver === 'undefined') return;

    var observer = new MutationObserver(function () {
      syncFooter(modal);
    });
    observer.observe(content, { childList: true, subtree: true });
    syncFooter(modal);
  }

  /**
   * Fill the input with a popular-search term and run the predictive search.
   */
  function runSuggestion(pill, event) {
    var input = getInput();
    var term = pill.getAttribute('data-grillion-search-term');
    if (!input || !term) return;

    event.preventDefault();
    input.value = term;

    var inputEvent;
    try {
      inputEvent = new InputEvent('input', { bubbles: true, inputType: 'insertText', data: term });
    } catch (e) {
      inputEvent = document.createEvent('Event');
      inputEvent.initEvent('input', true, true);
      try {
        Object.defineProperty(inputEvent, 'inputType', { value: 'insertText' });
      } catch (err) {
        /* noop */
      }
    }
    input.dispatchEvent(inputEvent);

    try {
      input.focus({ preventScroll: true });
    } catch (e) {
      input.focus();
    }
  }

  function bindModal() {
    var modal = getModal();
    if (!modal || modal.dataset.grillionSearchBound) return;
    modal.dataset.grillionSearchBound = 'true';

    var dialog = getDialog(modal);

    modal.addEventListener('dialog:close', function () {
      setTriggersExpanded(false);
    });

    if (dialog) {
      // Native close (e.g. Escape handled by the browser) keeps triggers in sync
      dialog.addEventListener('close', function () {
        setTriggersExpanded(false);
      });

      // Fallback backdrop click + Escape if dialog-component never upgraded
      dialog.addEventListener('click', function (event) {
        if (typeof modal.closeDialog === 'function') return;
        if (event.target === dialog) dialog.close();
      });
    }

    modal.addEventListener('click', function (event) {
      var target = event.target;
      if (!(target instanceof Element)) return;

      var pill = target.closest('[data-grillion-search-term]');
      if (pill) {
        runSuggestion(pill, event);
        return;
      }

      // Close button fallback while dialog-component is loading
      var closeBtn = target.closest('.predictive-search__close-modal-button');
      if (closeBtn && typeof modal.closeDialog !== 'function') {
        event.preventDefault();
        closeSearch();
      }
    });

    observeResults(modal);
  }

  function bindTriggers() {
    var triggers = document.querySelectorAll(OPEN_SELECTOR);
    for (var i = 0; i < triggers.length; i++) {
      var trigger = triggers[i];
      if (trigger.dataset.grillionSearchBound) continue;
      trigger.dataset.grillionSearchBound = 'true';
      trigger.addEventListener('click', openSearch);
      trigger.addEventListener('keydown', function (event) {
        if (event.key === ' ' || event.key === 'Spacebar') openSearch(event);
      });
    }
  }

  function init() {
    bindTriggers();
    bindModal();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-bind after the header section is re-rendered in the theme editor
  document.addEventListener('shopify:section:load', init);

  window.GrillionSearch = { open: openSearch, close: closeSearch };
})();
